/**
 * Pure UI state context for story content "meta" — which content block is
 * selected, its display order, and which is in the viewport.
 *
 * Replaces the `meta` slice of `storyViewState` (compose-app). No server
 * round-trips. Use `useReducer` per Rule 5 (React Context + useReducer for
 * state shared across multiple component levels).
 */

import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { IStoryContent } from '@opengov/stories-shared/types';

type StoryContentMetaState = {
  storyContent: IStoryContent;
  storyContentOrder: number;
  storyContentInView: string;
};

type StoryContentMetaAction =
  | { type: 'SET_STORY_CONTENT'; payload: IStoryContent }
  | { type: 'SET_STORY_CONTENT_ORDER'; payload: number }
  | { type: 'SET_STORY_CONTENT_IN_VIEW'; payload: string };

const DEFAULT_STORY_CONTENT_ORDER = 2;

const initialState: StoryContentMetaState = {
  storyContent: {} as IStoryContent,
  storyContentOrder: DEFAULT_STORY_CONTENT_ORDER,
  storyContentInView: '',
};

function reducer(
  state: StoryContentMetaState,
  action: StoryContentMetaAction
): StoryContentMetaState {
  switch (action.type) {
    case 'SET_STORY_CONTENT':
      return { ...state, storyContent: action.payload };
    case 'SET_STORY_CONTENT_ORDER':
      return { ...state, storyContentOrder: action.payload };
    case 'SET_STORY_CONTENT_IN_VIEW':
      return { ...state, storyContentInView: action.payload };
    default:
      return state;
  }
}

export type StoryContentMetaContextValue = StoryContentMetaState & {
  setStoryContent(storyContent: IStoryContent): void;
  setStoryContentOrder(order: number): void;
  setStoryContentInView(id: string): void;
};

const StoryContentMetaContext = createContext<StoryContentMetaContextValue | undefined>(undefined);

type Props = { children: ReactNode };

export function StoryContentMetaProvider({ children }: Props) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const value: StoryContentMetaContextValue = {
    ...state,
    setStoryContent: (payload) => dispatch({ type: 'SET_STORY_CONTENT', payload }),
    setStoryContentOrder: (payload) => dispatch({ type: 'SET_STORY_CONTENT_ORDER', payload }),
    setStoryContentInView: (payload) => dispatch({ type: 'SET_STORY_CONTENT_IN_VIEW', payload }),
  };

  return (
    <StoryContentMetaContext.Provider value={value}>
      {children}
    </StoryContentMetaContext.Provider>
  );
}

export function useStoryContentMeta(): StoryContentMetaContextValue {
  const ctx = useContext(StoryContentMetaContext);
  if (!ctx) {
    throw new Error('useStoryContentMeta must be used within StoryContentMetaProvider');
  }
  return ctx;
}
